import {
  Button,
  buttonStylesGhost,
  Loader,
  loaderStylesSpinnerXLarge,
  PageHeader,
  pageHeaderStylesSecondary,
  useClassNames,
  useToast,
} from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { EditFeaturePanel } from '../../../components/EditFeaturePanel/EditFeaturePanel';
import { FeatureDetails } from '../../../components/FeatureDetails/FeatureDetails';
import { fsApi } from '../../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  Feature as FeatureData,
  FeatureResponse,
  ListableFeatureSet,
  ListableFeatureSetResponse,
} from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { getErrorMessageBarMessage } from '../../../featurestore/utils';
import { RoutePaths } from '../../../pages/Routes';
import { featureStylesDefault, IFeatureClassNames, IFeatureStyles } from './Feature.styles';

const canEdit = (data: ListableFeatureSet = {}) =>
  data.permission === ActivePermission.ACTIVE_PERMISSION_OWNER ||
  data.permission === ActivePermission.ACTIVE_PERMISSION_EDITOR;

export const Feature = () => {
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showEditFeaturePanel, setShowEditFeaturePanel] = useState(false);
  const [data, setData] = useState<{ feature?: FeatureData; listableFeatureSet?: ListableFeatureSet }>();
  const { featureId, featureSetId } = useParams<{ featureId: string; featureSetId: string }>();
  const history = useHistory();
  const { addToast } = useToast();
  const load = useCallback(
    async (updatedFeature?: FeatureData) => {
      setLoadingMessage(`Loading Feature`);
      try {
        const { listableFeatureSet }: ListableFeatureSetResponse = await fsApi(CoreService.GetListableFeatureSet, {
          featureSetId,
        });
        const newFeatureId = updatedFeature ? updatedFeature.id : featureId;
        const { feature }: FeatureResponse = await fsApi(CoreService.GetFeature, {
          featureId: newFeatureId,
          featureSetId,
          featureSetVersion: listableFeatureSet?.version,
        });
        setData({ feature, listableFeatureSet });
      } catch (err) {
        addToast(getErrorMessageBarMessage(err, `An error has occurred while loading Feature: `));
      } finally {
        setLoadingMessage('');
      }
    },
    [addToast, featureId, featureSetId]
  );
  useEffect(() => {
    load();
  }, [load]);
  const classNames = useClassNames<IFeatureStyles, IFeatureClassNames>(
    'Feature',
    featureStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return loadingMessage ? (
    <Loader styles={loaderStylesSpinnerXLarge} label={loadingMessage} />
  ) : (
    <div className={classNames.root} data-test="feature">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        style={{ marginBottom: 24 }}
        pageTitle={data?.feature?.name}
        breadcrumbProps={{
          items: [
            {
              text: data?.listableFeatureSet?.projectName || '',
              key: 'Project Name',
              onClick: () =>
                history.push(
                  `${RoutePaths.FEATURESTORE_PROJECT}/${data?.listableFeatureSet?.projectId}?name=${data?.listableFeatureSet?.projectName}`
                ),
            },
            {
              text: data?.listableFeatureSet?.name || '',
              key: 'Feature set Name',
              onClick: () => history.push(`${RoutePaths.FEATURESTORE_FEATURE_SET}/${data?.listableFeatureSet?.id}`),
            },
            {
              text: data?.feature?.name || '',
              key: 'Feature Name',
              isCurrentItem: true,
            },
          ],
        }}
        actions={
          canEdit(data?.listableFeatureSet) ? (
            <Button
              style={{ marginRight: 8 }}
              styles={buttonStylesGhost}
              text="Edit"
              iconName="Edit"
              onClick={() => setShowEditFeaturePanel(true)}
            />
          ) : undefined
        }
      />
      <FeatureDetails data={data?.feature || {}} fullScreen />
      <EditFeaturePanel
        projectId={data?.listableFeatureSet?.projectId}
        featureSetVersion={data?.listableFeatureSet?.version}
        feature={data?.feature}
        show={showEditFeaturePanel}
        onDismiss={(_e, updatedFeatureSet, updatedFeature) => {
          if (updatedFeatureSet && updatedFeature) {
            load(updatedFeature);
          }
          setShowEditFeaturePanel(false);
        }}
      />
    </div>
  );
};
